import React, { useState, useEffect } from 'react';
import nubiecloudMark from '../assets/nubiecloud-mark.png';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import { Button, Input } from '../components/ui';
import Logger from '../utils/logger';

interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

const ResetPasswordPage: React.FC = () => {
  const { t } = (useTranslation as any)('auth');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<ResetPasswordForm>();
  const password = watch('password');

  useEffect(() => {
    // Recuperer le token et l'email depuis l'URL
    const tokenFromUrl = searchParams.get('token');
    const emailFromUrl = searchParams.get('email');

    if (!tokenFromUrl || !emailFromUrl) {
      toast.error(t('resetPassword.invalidLink'));
      navigate('/forgot-password');
      return;
    }

    setToken(tokenFromUrl);
    setEmail(emailFromUrl);

    // Scrub sensitive params from URL to prevent leakage via browser history/Referer
    window.history.replaceState({}, '', '/reset-password');
  }, [searchParams, navigate, t]);

  const onSubmit = async (data: ResetPasswordForm) => {
    if (data.password !== data.confirmPassword) {
      toast.error(t('password.mismatch', { ns: 'validation' }));
      return;
    }

    if (!token || !email) {
      toast.error(t('resetPassword.missingInfo'));
      return;
    }

    setLoading(true);
    try {
      await apiService.resetPassword({
        email: email,
        token: token,
        nouveau_mot_de_passe: data.password
      });

      toast.success(t('resetPassword.success'));

      // Rediriger vers la page de login avec un message
      navigate('/login', {
        state: {
          message: t('resetPassword.successRedirect'),
          email: email
        }
      });
    } catch (error: unknown) {
      Logger.error('Erreur reinitialisation:', error);
      const apiError = error as { response?: { status?: number; data?: { detail?: string } } };
      let errorMessage = t('resetPassword.error');

      if (apiError.response?.status === 400) {
        errorMessage = t('resetPassword.tokenExpired');
      } else if (apiError.response?.data?.detail) {
        errorMessage = apiError.response.data.detail;
      }

      toast.error(errorMessage);

      // Si le token est invalide, rediriger vers la page de demande
      if (apiError.response?.status === 400) {
        setTimeout(() => {
          navigate('/forgot-password');
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex items-center space-x-2">
            <img src={nubiecloudMark} alt="" className="w-8 h-8 object-contain" />
            <span className="text-2xl font-bold text-text-primary">NubieCloud</span>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-text-primary">
          {t('resetPassword.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary">
          {t('resetPassword.subtitle')}{' '}
          <span className="font-medium text-accent-400">{email}</span>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-2 py-8 px-4 shadow-card sm:rounded-card sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Nouveau mot de passe */}
            <Input
              {...register('password', {
                required: t('password.required', { ns: 'validation' }),
                minLength: {
                  value: 8,
                  message: t('password.minLength', { ns: 'validation', count: 8 })
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: t('password.pattern', { ns: 'validation' })
                }
              })}
              id="password"
              type="password"
              label={t('resetPassword.newPassword')}
              autoComplete="new-password"
              placeholder="••••••••"
              error={errors.password?.message}
            />

            {/* Confirmer le mot de passe */}
            <Input
              {...register('confirmPassword', {
                required: t('password.confirmRequired', { ns: 'validation' }),
                validate: (value) => value === password || t('password.mismatch', { ns: 'validation' })
              })}
              id="confirmPassword"
              type="password"
              label={t('resetPassword.confirmPassword')}
              autoComplete="new-password"
              placeholder="••••••••"
              error={errors.confirmPassword?.message}
            />

            {/* Exigences du mot de passe */}
            <div className="text-sm text-text-secondary">
              <p className="font-medium mb-2">{t('resetPassword.requirements.title')}</p>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('resetPassword.requirements.minLength')}</li>
                <li>{t('resetPassword.requirements.lowercase')}</li>
                <li>{t('resetPassword.requirements.uppercase')}</li>
                <li>{t('resetPassword.requirements.digit')}</li>
              </ul>
            </div>

            {/* Submit Button */}
            <div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={loading}
                disabled={loading}
                className="w-full"
              >
                {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
              </Button>
            </div>

            {/* Retour */}
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-text-secondary hover:text-text-primary"
              >
                {t('resetPassword.backToLogin')}
              </Link>
            </div>
          </form>

          {/* Info Securite */}
          <div className="mt-6 p-4 bg-blue-500/12 rounded-md border border-blue-500/20">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-accent-400">
                  {t('resetPassword.securityTitle')}
                </h3>
                <div className="mt-2 text-sm text-accent-400">
                  <p>
                    {t('resetPassword.securityMessage')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
